(function () {
  const builderSelector = '[data-dtf-transfer-size-builder]';
  const allowedTypes = ['image/png', 'image/jpeg', 'image/webp'];
  const maxFileBytes = 25 * 1024 * 1024;
  const storagePrefix = 'inknfiber-dtf-transfer-size-';
  const minSize = 1;
  const maxSize = 22;

  const backendBaseUrl = (() => {
    const configuredUrl = String(window.InkNfiberConfig?.backendUrl || '').trim();
    if (!configuredUrl) return '';

    try {
      return new URL(configuredUrl, window.location.origin).toString().replace(/\/$/, '');
    } catch (_error) {
      return '';
    }
  })();

  const backendEndpoint = (path) => {
    const normalizedPath = String(path || '').startsWith('/') ? String(path || '') : `/${path || ''}`;
    return backendBaseUrl ? `${backendBaseUrl}${normalizedPath}` : normalizedPath;
  };
  const customDesignFileUploadEndpoint = backendEndpoint('/custom-design-file-upload');
  const aiDesignGenerateEndpoint = backendEndpoint('/ai-design-generate');
  const aiBackgroundRemoveEndpoint = backendEndpoint('/ai-background-remove');
  const aiPerformanceDimensions = { fast: 1024, balanced: 1536, quality: 2048 };
  const aiSessionCache = window.InkNfiberAiSessionCache || (window.InkNfiberAiSessionCache = {
    designs: new Map(),
    backgrounds: new Map(),
  });
  const aiPromptHelper = window.InkNfiberAiDesignPromptHelper || (window.InkNfiberAiDesignPromptHelper = (() => {
    const styleInstructions = {
      'Clean Vector': 'Clean vector illustration, bold clean outlines',
      Vintage: 'Vintage illustration with lightly distressed print texture',
      Cartoon: 'Original cartoon illustration with expressive shapes',
      Minimal: 'Minimal graphic design with simple bold shapes',
      'Bold Graphic': 'Bold graphic illustration with strong contrast',
      Mascot: 'Original mascot illustration with dynamic character energy',
      Retro: 'Retro inspired illustration with classic print-shop styling',
      'Realistic Illustration': 'Realistic illustration with polished print-ready detail',
    };
    const outputIntros = {
      'T-Shirt Design': 'Original t-shirt design of',
      'DTF Transfer': 'Original DTF transfer design of',
      'Sticker Style': 'Original sticker-style design of',
      'Logo Style': 'Original logo-style mark of',
      'Mascot Design': 'Original mascot design of',
    };
    const riskyTerms = [
      'adidas', 'barbie', 'batman', 'celebrity', 'coca cola', 'coca-cola', 'disney', 'donald duck',
      'dragon ball', 'fortnite', 'harry potter', 'hello kitty', 'logo', 'marvel', 'mickey',
      'mickey mouse', 'minecraft', 'nba', 'nfl', 'nike', 'nintendo', 'pokemon',
      'spongebob', 'star wars', 'super mario', 'superman', 'taylor swift', 'trademark',
    ];
    const warning = 'This may be rejected because it references a brand, copyrighted character, celebrity, or logo. Try describing an original design instead.';
    const normalizeOption = (value, options, fallback) => (options.includes(String(value || '').trim()) ? String(value || '').trim() : fallback);

    return {
      buildAiDesignPrompt(userPrompt, options = {}) {
        const originalPrompt = String(userPrompt || '').trim();
        const style = normalizeOption(options.style, Object.keys(styleInstructions), 'Clean Vector');
        const outputType = normalizeOption(options.outputType, Object.keys(outputIntros), 'T-Shirt Design');
        const includeText = Boolean(options.includeText);
        const enhancedPrompt = originalPrompt
          ? `${outputIntros[outputType]} ${originalPrompt}. ${styleInstructions[style]}, centered composition, high contrast, print-ready DTF artwork, transparent background. ${includeText ? 'If text is requested, keep lettering clean, readable, and original.' : 'No text, no letters, no words.'} Avoid brand names, copyrighted characters, celebrities, logos, and trademarked designs.`
          : '';
        return {
          originalPrompt,
          enhancedPrompt,
          style,
          outputType,
          includeText,
          warning: this.getAiPromptSafetyWarning(originalPrompt),
        };
      },
      getAiPromptSafetyWarning(userPrompt) {
        const prompt = String(userPrompt || '').toLowerCase();
        return riskyTerms.some((term) => prompt.includes(term)) ? warning : '';
      },
    };
  })());

  const defaultPopularSizes = [
    { label: 'Full Front - 12" x 14"', width: 12, height: 14 },
    { label: 'Medium Front - 8" x 8"', width: 8, height: 8 },
    { label: 'Center Chest - 5" x 5"', width: 5, height: 5 },
    { label: 'Across Chest - 12" x 4"', width: 12, height: 4 },
    { label: 'Right Chest - 5" x 5"', width: 5, height: 5 },
    { label: 'Left Chest - 5" x 5"', width: 5, height: 5 },
    { label: 'Right Sleeve - 3.5" x 3.5"', width: 3.5, height: 3.5 },
    { label: 'Left Sleeve - 3.5" x 3.5"', width: 3.5, height: 3.5 },
    { label: 'Right Vertical - 5" x 14"', width: 5, height: 14 },
    { label: 'Left Vertical - 5" x 14"', width: 5, height: 14 },
    { label: 'Front Bottom Right - 5" x 6"', width: 5, height: 6 },
    { label: 'Front Bottom Left - 5" x 6"', width: 5, height: 6 },
    { label: 'Full Back - 12" x 14"', width: 12, height: 14 },
    { label: 'Medium Back - 8" x 8"', width: 8, height: 8 },
    { label: 'Locker Patch Area - 4" x 4"', width: 4, height: 4 },
    { label: 'Across Shoulders - 14" x 4"', width: 14, height: 4 },
  ];

  const defaultPricingConfig = {
    customSizeUsesTierPricing: true,
    customSizeReferenceWidth: 3.74,
    customSizeReferenceHeight: 4.67,
    customSizeSquareInchRate: null,
    customSizeMinPrice: 1.99,
  };

  const defaultPricingTiers = [
    { min: 1, max: 14, pricePerUnit: 0.70, minUnitPrice: 1.99, discount: '-' },
    { min: 15, max: 49, pricePerUnit: 0.61, minUnitPrice: 1.75, discount: '20% off' },
    { min: 50, max: 99, pricePerUnit: 0.52, minUnitPrice: 1.50, discount: '30% off' },
    { min: 100, max: 249, pricePerUnit: 0.45, minUnitPrice: 1.25, discount: '40% off' },
    { min: 250, max: Infinity, pricePerUnit: 0.38, minUnitPrice: 1.00, discount: '50% off' },
  ];

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function round(value, places = 2) {
    const factor = 10 ** places;
    return Math.round(value * factor) / factor;
  }

  function money(value) {
    return `$${round(value, 2).toFixed(2)}`;
  }

  function inchLabel(value) {
    const rounded = round(value, 2);
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
  }

  function safeId(prefix = 'dtf-transfer') {
    if (window.crypto?.randomUUID) return `${prefix}-${window.crypto.randomUUID()}`;
    return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }

  function validVariantId(value) {
    return Boolean(value && /^\d+$/.test(String(value)));
  }

  function escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function parsePositiveNumber(value, fallback = null) {
    const normalizedValue = typeof value === 'string' ? value.replace(/[$,\s]/g, '') : value;
    const parsed = Number(normalizedValue);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
  }

  function parseQty(value, fallback = 1) {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
  }

  function parseTierMax(value) {
    if (value === '' || value === null || value === undefined) return Infinity;
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : Infinity;
  }

  function normalizedDiscount(value) {
    const discount = String(value || '').trim();
    return discount && discount !== '-' ? discount : '-';
  }

  function isStandardDiscount(value) {
    const discount = normalizedDiscount(value);
    return discount === '-' || discount === '0% off';
  }

  function tierLabel(tier) {
    if (tier.label) return tier.label;
    return tier.max === Infinity ? `${tier.min}+` : `${tier.min}-${tier.max}`;
  }

  function fileToDataUrl(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error(`Unable to read ${file.name}.`));
      reader.readAsDataURL(file);
    });
  }

  function imageFromSrc(src) {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('Unable to load uploaded artwork preview.'));
      image.src = src;
    });
  }

  function canvasToBlob(canvas, type = 'image/png', quality = 0.92) {
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Unable to generate preview image.'));
      }, type, quality);
    });
  }

  function normalizeAiPerformanceMode(value) {
    const mode = String(value || '').trim().toLowerCase();
    return ['fast', 'balanced', 'quality'].includes(mode) ? mode : 'fast';
  }

  function aiMaxDimensionForMode(mode) {
    return aiPerformanceDimensions[normalizeAiPerformanceMode(mode)] || aiPerformanceDimensions.fast;
  }

  function aiFileCacheKey(file, mode, scope = 'background') {
    return [
      scope,
      normalizeAiPerformanceMode(mode),
      file?.name || 'artwork',
      file?.type || '',
      file?.size || 0,
      file?.lastModified || 0,
    ].join(':');
  }

  function aiPromptCacheKey(prompt, mode, scope = 'design') {
    return [scope, normalizeAiPerformanceMode(mode), String(prompt || '').trim().toLowerCase()].join(':');
  }

  async function optimizeImageForAi(file, maxDimension) {
    if (!file || !allowedTypes.includes(file.type) || !maxDimension || maxDimension <= 0) {
      return { file, optimized: false };
    }

    let imageBitmap = null;
    let image = null;
    let objectUrl = '';

    try {
      if ('createImageBitmap' in window) {
        imageBitmap = await createImageBitmap(file);
      } else {
        objectUrl = URL.createObjectURL(file);
        image = await new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve(img);
          img.onerror = () => reject(new Error('Unable to prepare image before AI processing.'));
          img.src = objectUrl;
        });
      }

      const sourceWidth = imageBitmap?.width || image?.naturalWidth || image?.width || 0;
      const sourceHeight = imageBitmap?.height || image?.naturalHeight || image?.height || 0;
      const longestSide = Math.max(sourceWidth, sourceHeight);
      if (!sourceWidth || !sourceHeight || longestSide <= maxDimension) {
        return { file, optimized: false, width: sourceWidth, height: sourceHeight };
      }

      const scale = maxDimension / longestSide;
      const width = Math.max(1, Math.round(sourceWidth * scale));
      const height = Math.max(1, Math.round(sourceHeight * scale));
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d');
      if (!context) return { file, optimized: false, width: sourceWidth, height: sourceHeight };
      context.drawImage(imageBitmap || image, 0, 0, width, height);

      const outputType = file.type === 'image/jpeg' ? 'image/jpeg' : 'image/png';
      const blob = await canvasToBlob(canvas, outputType, 0.86);
      const extension = outputType === 'image/jpeg' ? 'jpg' : 'png';
      const baseName = (file.name || 'artwork').replace(/\.[a-z0-9]+$/i, '') || 'artwork';
      return {
        file: new File([blob], `${baseName}-ai-${maxDimension}.${extension}`, { type: outputType }),
        optimized: true,
        width,
        height,
        originalWidth: sourceWidth,
        originalHeight: sourceHeight,
      };
    } catch (_error) {
      return { file, optimized: false };
    } finally {
      if (imageBitmap?.close) imageBitmap.close();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    }
  }

  function absoluteWorkerUrl(pathOrUrl) {
    return new URL(pathOrUrl || '/', backendBaseUrl || window.location.origin).toString();
  }

  function supportRequestSuffix(body) {
    const requestId = String(body?.request_id || body?.requestId || '').trim();
    return requestId ? ` Request ID: ${requestId}` : '';
  }

  function friendlyAiError(body, fallback) {
    const raw = String(body?.user_error || body?.error || body?.message || fallback || '').trim();
    const lower = raw.toLowerCase();
    const likelyPolicyBlock = ['copyright', 'trademark', 'policy', 'moderation', 'safety', 'blocked', 'rejected', 'not allowed', 'disallowed']
      .some((needle) => lower.includes(needle));
    const message = likelyPolicyBlock
      ? 'This prompt could not be processed. Try describing the design more generically without specific copyrighted character names.'
      : raw || fallback || 'The AI request could not be completed. Please try again.';
    return `${message}${supportRequestSuffix(body)}`;
  }

  function dispatchCartUpdate(cart) {
    if (window.InkNfiberCart?.dispatchCartUpdate) {
      window.InkNfiberCart.dispatchCartUpdate(cart, { source: 'inknfiber-dtf-transfers-by-size' });
      return;
    }

    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: 'inknfiber-dtf-transfers-by-size',
          data: {
            itemCount: cart?.item_count || 0,
            source: 'inknfiber-dtf-transfers-by-size',
          },
        },
      })
    );
  }

  function readConfig(builder) {
    const configNode = builder.querySelector('[data-dtf-size-config-json]');
    const parsedConfig = (() => {
      if (!configNode?.textContent) return {};
      try {
        return JSON.parse(configNode.textContent);
      } catch (error) {
        console.warn('[InkNfiber DTF Transfers By Size] Pricing config could not be parsed.', error);
        return {};
      }
    })();

    const pricingConfig = {
      customSizeUsesTierPricing: parsedConfig.customSizeUsesTierPricing !== false,
      customSizeReferenceWidth: parsePositiveNumber(
        parsedConfig.customSizeReferenceWidth,
        defaultPricingConfig.customSizeReferenceWidth
      ),
      customSizeReferenceHeight: parsePositiveNumber(
        parsedConfig.customSizeReferenceHeight,
        defaultPricingConfig.customSizeReferenceHeight
      ),
      customSizeSquareInchRate: parsePositiveNumber(parsedConfig.customSizeSquareInchRate, null),
      customSizeMinPrice: parsePositiveNumber(parsedConfig.customSizeMinPrice, defaultPricingConfig.customSizeMinPrice),
    };
    const referenceArea = Math.max(pricingConfig.customSizeReferenceWidth * pricingConfig.customSizeReferenceHeight, 1);
    const tierSource = Array.isArray(parsedConfig.pricingTiers) && parsedConfig.pricingTiers.length
      ? parsedConfig.pricingTiers
      : defaultPricingTiers;
    const pricingTiers = tierSource
      .map((tier, index) => {
        const fallback = defaultPricingTiers[index] || defaultPricingTiers[defaultPricingTiers.length - 1];
        const min = parseQty(tier.min, fallback.min);
        const max = parseTierMax(tier.max ?? fallback.max);
        const pricePerUnit = parsePositiveNumber(tier.pricePerUnit, fallback.pricePerUnit);
        const minUnitPrice = parsePositiveNumber(tier.minUnitPrice, fallback.minUnitPrice);
        const discount = normalizedDiscount(tier.discount || fallback.discount);

        return {
          min,
          max: max >= min ? max : Infinity,
          label: max === Infinity ? `${min}+` : `${min}-${max}`,
          pricePerUnit,
          ratePerSqIn: pricePerUnit / referenceArea,
          minUnitPrice,
          discount,
        };
      })
      .sort((a, b) => a.min - b.min);

    const hasConfiguredPopularSizes = Array.isArray(parsedConfig.popularSizes) && parsedConfig.popularSizes.length > 0;
    const popularSource = hasConfiguredPopularSizes ? parsedConfig.popularSizes : defaultPopularSizes;
    const popularSizes = popularSource
      .filter((size) => size.enabled !== false)
      .map((size) => ({
        label: String(size.label || '').trim(),
        width: parsePositiveNumber(size.width, null),
        height: parsePositiveNumber(size.height, null),
        priceOverride: parsePositiveNumber(size.priceOverride, null),
      }))
      .filter((size) => size.label && size.width && size.height);

    return {
      pricingConfig,
      pricingTiers: pricingTiers.length ? pricingTiers : fallbackPricingTiers(),
      popularSizes: popularSizes.length || hasConfiguredPopularSizes ? popularSizes : defaultPopularSizes,
    };
  }

  function fallbackPricingTiers() {
    const referenceArea = defaultPricingConfig.customSizeReferenceWidth * defaultPricingConfig.customSizeReferenceHeight;
    return defaultPricingTiers.map((tier) => ({
      ...tier,
      label: tier.max === Infinity ? `${tier.min}+` : `${tier.min}-${tier.max}`,
      ratePerSqIn: tier.pricePerUnit / referenceArea,
    }));
  }

  function tierForQty(pricingTiers, qty) {
    const tiers = Array.isArray(pricingTiers) && pricingTiers.length ? pricingTiers : fallbackPricingTiers();
    return tiers.find((tier) => qty >= tier.min && qty <= tier.max) || tiers[0];
  }

  function priceForTier(width, height, qty, options = {}) {
    const pricingTiers = Array.isArray(options.pricingTiers) && options.pricingTiers.length
      ? options.pricingTiers
      : fallbackPricingTiers();
    const tier = options.tier || tierForQty(pricingTiers, qty);
    const sizeMode = options.sizeMode || 'custom';
    const popularSize = options.popularSize || null;
    const pricingConfig = options.pricingConfig || defaultPricingConfig;
    const area = width * height;
    const customTierPricingDisabled = sizeMode === 'custom' && pricingConfig.customSizeUsesTierPricing === false;
    let unit = popularSize?.priceOverride || null;

    if (!unit && customTierPricingDisabled && pricingConfig.customSizeSquareInchRate) {
      unit = Math.max(area * pricingConfig.customSizeSquareInchRate, pricingConfig.customSizeMinPrice || 0);
    }

    if (!unit) {
      const pricingTier = customTierPricingDisabled ? pricingTiers[0] : tier;
      unit = Math.max(area * pricingTier.ratePerSqIn, pricingTier.minUnitPrice);
    }

    const unitCents = Math.round(unit * 100);
    const totalCents = unitCents * qty;

    return {
      area,
      tier,
      unitPrice: unitCents / 100,
      total: totalCents / 100,
      unitCents,
      totalCents,
    };
  }

  function findMatchingPopular(popularSizes, width, height) {
    return popularSizes.find((size) => Math.abs(size.width - width) < 0.005 && Math.abs(size.height - height) < 0.005);
  }

  function initBuilder(builder) {
    if (builder.dataset.initialized === 'true') return;
    builder.dataset.initialized = 'true';

    const productResolved = builder.dataset.productResolved === 'true';
    const productVariantId = String(builder.dataset.productVariantId || '');
    const productAvailable = builder.dataset.productAvailable === 'true';
    const priceProductResolved = builder.dataset.priceProductResolved === 'true';
    const priceVariantId = String(builder.dataset.priceVariantId || '');
    const priceProductAvailable = builder.dataset.priceProductAvailable === 'true';
    const priceAdjustmentCents = Number(builder.dataset.priceAdjustmentCents || 0);
    const estimatorCheckoutPricingEnabled = false;
    const estimatorCheckoutPriceMode = String(builder.dataset.estimatorCheckoutPriceMode || 'estimate_or_tier_fallback');
    const uploadEndpoint = String(builder.dataset.uploadEndpoint || '').trim();
    const r2Enabled = builder.dataset.r2Enabled === 'true';
    const debugParams = new URLSearchParams(window.location.search);
    const reorderId = debugParams.get('reorder');
    const reorderToken = debugParams.get('reorder_token');
    const debug = debugParams.get('designer_debug') === '1' || debugParams.get('dtf_debug') === '1';
    const configDiagnostics = {
      transferSettingRaw: builder.dataset.productSettingRaw || '',
      transferProductHandle: builder.dataset.productHandle || '',
      transferProductTitle: builder.dataset.productTitle || '',
      transferProductId: builder.dataset.productId || '',
      transferSelectedVariantId: builder.dataset.productSelectedVariantId || '',
      transferFirstAvailableVariantId: builder.dataset.productFirstAvailableVariantId || '',
      transferFirstVariantId: builder.dataset.productFirstVariantId || '',
      transferAvailableVariantCount: Number(builder.dataset.productAvailableVariantCount || 0),
      transferProductAvailable: builder.dataset.productObjectAvailable === 'true',
      transferProductResolved: productResolved,
      transferVariantId: productVariantId,
      transferVariantAvailable: productAvailable,
      transferFinalVariantIdPopulated: validVariantId(productVariantId),
      adjustmentSettingRaw: builder.dataset.priceProductSettingRaw || '',
      adjustmentProductHandle: builder.dataset.priceProductHandle || '',
      adjustmentProductTitle: builder.dataset.priceProductTitle || '',
      adjustmentProductId: builder.dataset.priceProductId || '',
      adjustmentSelectedVariantId: builder.dataset.priceSelectedVariantId || '',
      adjustmentFirstAvailableVariantId: builder.dataset.priceFirstAvailableVariantId || '',
      adjustmentFirstVariantId: builder.dataset.priceFirstVariantId || '',
      adjustmentAvailableVariantCount: Number(builder.dataset.priceAvailableVariantCount || 0),
      adjustmentProductAvailable: builder.dataset.priceProductObjectAvailable === 'true',
      adjustmentProductResolved: priceProductResolved,
      adjustmentVariantId: priceVariantId,
      adjustmentVariantAvailable: priceProductAvailable,
      adjustmentFinalVariantIdPopulated: validVariantId(priceVariantId),
      adjustmentPriceCents: priceAdjustmentCents,
    };
    console.debug('[InkNfiber DTF config]', configDiagnostics);
    const artworkChecker = window.InkNfiberArtworkQualityChecker || null;
    const artworkQualityContainer = builder.querySelector('[data-artwork-quality-check]');
    const artworkCheckerEnabled = builder.dataset.artworkCheckerEnabled !== 'false';
    const artworkCheckerOptions = {
      maxFileSizeMb: Number(builder.dataset.artworkCheckerMaxFileSizeMb || 25) || 25,
      minRecommendedLongestSide: Number(builder.dataset.artworkCheckerMinLongestSide || 1000) || 1000,
      requirePass: builder.dataset.requireArtworkQualityPass !== 'false',
      requireTransparent: builder.dataset.requireTransparentBackground !== 'false',
      debug,
      fileSizeBlocks: true,
    };
    const aiPhotoshootEnabled = builder.dataset.aiPhotoshootEnabled === 'true';
    const aiBackgroundRemoverEnabled = builder.dataset.aiBackgroundRemoverEnabled === 'true';
    const aiPerformanceMode = normalizeAiPerformanceMode(builder.dataset.aiPerformanceMode);
    const aiBackgroundMaxDimension = aiMaxDimensionForMode(aiPerformanceMode);
    const { pricingConfig, pricingTiers, popularSizes } = readConfig(builder);
    const uploadInput = builder.querySelector('[data-dtf-size-upload]');
    const addDesignButton = builder.querySelector('[data-dtf-size-add-design]');
    const fileLabel = builder.querySelector('[data-dtf-size-file]');
    const designsContainer = builder.querySelector('[data-dtf-size-designs]');
    const summaryDesigns = builder.querySelector('[data-dtf-size-summary-designs]');
    const summaryQty = builder.querySelector('[data-dtf-size-summary-qty]');
    const summaryTransfer = builder.querySelector('[data-dtf-size-summary-transfer]');
    const summaryArtwork = builder.querySelector('[data-dtf-size-summary-artwork]');
    const summaryTotal = builder.querySelector('[data-dtf-size-summary-total]');
    const summaryList = builder.querySelector('[data-dtf-size-summary-list]');
    const summaryEstimate = builder.querySelector('[data-dtf-size-summary-estimate]');
    const status = builder.querySelector('[data-dtf-size-status]');
    const configMessage = builder.querySelector('[data-dtf-size-config]');
    const addButton = builder.querySelector('[data-dtf-size-add]');
    const viewCartLink = builder.querySelector('[data-dtf-size-view-cart]');
    const checkoutLink = builder.querySelector('[data-dtf-size-checkout]');
    const aiPromptInput = builder.querySelector('[data-dtf-size-ai-prompt]');
    const aiDesignStyleSelect = builder.querySelector('[data-ai-design-style]');
    const aiOutputTypeSelect = builder.querySelector('[data-ai-output-type]');
    const aiIncludeTextInput = builder.querySelector('[data-ai-include-text]');
    const aiPromptWarning = builder.querySelector('[data-ai-prompt-warning]');
    const aiGenerateButton = builder.querySelector('[data-dtf-size-ai-generate]');
    const aiStatus = builder.querySelector('[data-dtf-size-ai-status]');
    const aiPreview = builder.querySelector('[data-dtf-size-ai-preview]');
    const aiPreviewImage = builder.querySelector('[data-dtf-size-ai-preview-image]');
    const aiAddButton = builder.querySelector('[data-dtf-size-ai-add]');
    const artworkEstimator = window.InkNfiberArtworkCostEstimator || null;
    const artworkEstimatorConfig = artworkEstimator?.configFromDataset?.(builder.dataset, 3) || null;

    const state = {
      orderId: safeId('dtf-transfer-order'),
      designs: [],
      adding: false,
    };
    let generatedAiDesign = null;
    let zoomModal = null;
    let zoomPreviouslyFocused = null;
    const artworkEstimateTimers = new Map();

    function debugLog(message, details = {}) {
      if (!debug) return;
      console.debug('[InkNfiber DTF Transfers By Size]', message, details);
    }

    function setStatus(message, tone = 'info') {
      if (!status) return;
      status.hidden = !message;
      status.textContent = message || '';
      status.dataset.tone = tone;
    }

    function previewIcon(type) {
      if (type === 'trash') {
        return `
          <svg aria-hidden="true" viewBox="0 0 24 24" focusable="false">
            <path d="M9 3h6l1 2h4v2H4V5h4l1-2Z"></path>
            <path d="M7 9h2v10H7V9Zm4 0h2v10h-2V9Zm4 0h2v10h-2V9Z"></path>
            <path d="M6 7h12l-1 14H7L6 7Z" fill="none" stroke="currentColor" stroke-width="2"></path>
          </svg>
        `;
      }

      return `
        <svg aria-hidden="true" viewBox="0 0 24 24" focusable="false">
          <circle cx="10.5" cy="10.5" r="6.5" fill="none" stroke="currentColor" stroke-width="2"></circle>
          <path d="m15.5 15.5 5 5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"></path>
          <path d="M10.5 7.5v6M7.5 10.5h6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"></path>
        </svg>
      `;
    }

    function measurementMarkup(design) {
      return {
        width: `${inchLabel(design.width)}"`,
        height: `${inchLabel(design.height)}"`,
      };
    }

    function ensureZoomModal() {
      if (zoomModal) return zoomModal;

      zoomModal = document.createElement('div');
      zoomModal.className = 'inknfiber-dtf-size__zoom-modal';
      zoomModal.hidden = true;
      zoomModal.innerHTML = `
        <div class="inknfiber-dtf-size__zoom-backdrop" data-dtf-zoom-close></div>
        <div class="inknfiber-dtf-size__zoom-dialog" role="dialog" aria-modal="true" aria-label="DTF transfer artwork preview">
          <button type="button" class="inknfiber-dtf-size__zoom-close" data-dtf-zoom-close aria-label="Close preview">x</button>
          <div class="inknfiber-dtf-size__zoom-frame">
            <img alt="">
          </div>
          <div class="inknfiber-dtf-size__zoom-caption">
            <strong data-dtf-zoom-title></strong>
            <span data-dtf-zoom-size></span>
          </div>
        </div>
      `;
      document.body.appendChild(zoomModal);
      zoomModal.addEventListener('click', (event) => {
        if (event.target.closest('[data-dtf-zoom-close]')) closeZoomModal();
      });
      return zoomModal;
    }

    function openZoomModal(design) {
      if (!design?.fileDataUrl) return;
      const modal = ensureZoomModal();
      const image = modal.querySelector('img');
      const title = modal.querySelector('[data-dtf-zoom-title]');
      const size = modal.querySelector('[data-dtf-zoom-size]');
      const measurements = measurementMarkup(design);

      if (image) {
        image.src = design.fileDataUrl;
        image.alt = `${design.fileName} preview`;
      }
      if (title) title.textContent = design.fileName;
      if (size) size.textContent = `${measurements.width} W x ${measurements.height} H`;
      zoomPreviouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      modal.hidden = false;
      document.documentElement.classList.add('inknfiber-dtf-size__zoom-open');
      modal.querySelector('[data-dtf-zoom-close]')?.focus();
    }

    function closeZoomModal() {
      if (!zoomModal || zoomModal.hidden) return;
      zoomModal.hidden = true;
      document.documentElement.classList.remove('inknfiber-dtf-size__zoom-open');
      if (zoomPreviouslyFocused?.focus) zoomPreviouslyFocused.focus();
      zoomPreviouslyFocused = null;
    }

    function artworkFileKey(file) {
      if (!file) return '';
      return `${file.name}:${file.size}:${file.lastModified}`;
    }

    function hasMainProduct() {
      return validVariantId(productVariantId);
    }

    function hasPriceProduct() {
      return validVariantId(priceVariantId);
    }

    function hasValidConfig() {
      return productResolved
        && hasMainProduct()
        && productAvailable
        && priceProductResolved
        && hasPriceProduct()
        && priceProductAvailable
        && priceAdjustmentCents === 1;
    }

    function configErrorMessage() {
      if (!productResolved) return 'Select a DTF Transfers By Size product in the Theme Editor.';
      if (!hasMainProduct() || !productAvailable) {
        return 'DTF Transfers By Size product is selected but unavailable. Check product status, Online Store sales channel, variant, or inventory.';
      }
      if (!priceProductResolved) return 'Select a DTF transfer price adjustment product in the Theme Editor.';
      if (!hasPriceProduct() || !priceProductAvailable) {
        return 'Price adjustment product is selected but unavailable. Check product status, Online Store sales channel, variant, or inventory.';
      }
      if (priceAdjustmentCents !== 1) return 'Price adjustment product must be priced at $0.01.';
      return '';
    }

    function selectedPopularSize(design) {
      if (design.sizeMode !== 'popular' || !design.popularLabel) return null;
      return popularSizes.find((size) => size.label === design.popularLabel) || null;
    }

    function currentCalculation(design, qty = design.qty, tier = null) {
      return priceForTier(design.width, design.height, qty, {
        pricingTiers,
        tier: tier || tierForQty(pricingTiers, qty),
        sizeMode: design.sizeMode,
        popularSize: selectedPopularSize(design),
        pricingConfig,
      });
    }

    function calculatedDiscountLabel(baseUnitCents, currentUnitCents) {
      const base = Number(baseUnitCents || 0);
      const current = Number(currentUnitCents || 0);
      if (!base || !current || current >= base) return '-';
      const percent = Math.round(((base - current) / base) * 100);
      return percent > 0 ? `${percent}% off` : '-';
    }

    function discountLabelForTier(design, tier, calculation = null) {
      const baseTier = pricingTiers[0];
      if (!baseTier || !tier || tier === baseTier) return '-';
      const baseCalculation = currentCalculation(design, Math.max(design.qty, baseTier.min), baseTier);
      const tierCalculation = calculation || currentCalculation(design, Math.max(design.qty, tier.min), tier);
      return calculatedDiscountLabel(baseCalculation.unitCents, tierCalculation.unitCents);
    }

    function standardDiscountLabel(label) {
      return !label || label === '-' ? 'Standard pricing' : label;
    }

    function formatCents(cents) {
      return money(Math.max(0, Number(cents || 0)) / 100);
    }

    function estimatePriceCents(estimate) {
      return Math.max(0, Math.round(Number(estimate?.suggestedPrice || 0) * 100));
    }

    function showInternalEstimatorUi() {
      return Boolean(artworkEstimator && artworkEstimatorConfig?.enabled && artworkEstimatorConfig?.showInternalCost);
    }

    function estimatorCheckoutDetails(design, calculation = currentCalculation(design)) {
      const basePriceCents = Math.max(0, Number(calculation?.totalCents || 0));
      const suggestedPriceCents = estimatePriceCents(design?.artworkEstimate);
      const hasEstimatorPrice = suggestedPriceCents > 0;
      const finalPriceCents = basePriceCents;
      const adjustmentCents = 0;

      return {
        basePriceCents,
        suggestedPriceCents,
        hasEstimatorPrice,
        finalPriceCents,
        adjustmentCents,
      };
    }

    function designCheckoutDisplay(design, calculation = currentCalculation(design)) {
      const totalCents = estimatorCheckoutPricingEnabled
        ? estimatorCheckoutDetails(design, calculation).finalPriceCents
        : Math.max(0, Number(calculation?.totalCents || 0));
      const qty = parseQty(design?.qty, 1);

      return {
        totalCents,
        unitCents: qty > 0 ? Math.round(totalCents / qty) : 0,
      };
    }

    function applyDesignCheckoutDisplay(card, design, calculation = currentCalculation(design)) {
      const unit = card?.querySelector('[data-dtf-design-unit]');
      const total = card?.querySelector('[data-dtf-design-total]');
      const display = designCheckoutDisplay(design, calculation);

      if (unit) unit.textContent = formatCents(display.unitCents);
      if (total) total.textContent = formatCents(display.totalCents);
    }

    function validateEstimatorAdjustmentProduct(details) {
      if (!estimatorCheckoutPricingEnabled || !details.adjustmentCents) return '';
      if (!priceProductResolved || !hasPriceProduct() || !priceProductAvailable) {
        return 'Price adjustment product is not configured. Please contact store support.';
      }
      if (priceAdjustmentCents !== 1) {
        return 'Price adjustment product must be priced at $0.01. Please contact store support.';
      }
      return '';
    }

    function validatePriceAdjustmentProductForAmount(amountCents, label = 'price adjustment') {
      const quantity = Math.round(Number(amountCents || 0));
      if (quantity <= 0) return '';
      if (!priceProductResolved) return `Select a DTF transfer price adjustment product in the Theme Editor before adding ${label}.`;
      if (!hasPriceProduct()) return `The DTF transfer price adjustment product is missing a usable variant ID for ${label}.`;
      if (!priceProductAvailable) return `The DTF transfer price adjustment product is unavailable for ${label}. Check product status, Online Store sales channel, variant, or inventory.`;
      if (priceAdjustmentCents !== 1) return `The DTF transfer price adjustment product must be priced at $0.01 before adding ${label}.`;
      return '';
    }

    function validateCartItems(items) {
      if (!Array.isArray(items) || !items.length) return 'No cart items were prepared for this DTF transfer order.';

      const invalidItem = items.find((item) => {
        return !validVariantId(item?.id)
          || !Number.isInteger(Number(item?.quantity))
          || Number(item?.quantity) <= 0;
      });

      if (!invalidItem) return '';

      if (!validVariantId(invalidItem?.id)) return 'A DTF transfer cart line is missing a valid Shopify variant ID.';
      return 'A DTF transfer cart line has an invalid quantity. Review the selected size, quantity, and price adjustment product.';
    }

    function cartApiErrorMessage(error, fallback = 'Unable to add DTF transfers to cart.') {
      const response = error?.response || {};
      return response.description
        || response.message
        || error?.message
        || fallback;
    }

    function estimatorCheckoutMarkup(design, calculation = currentCalculation(design)) {
      if (!estimatorCheckoutPricingEnabled || !showInternalEstimatorUi()) return '';
      const details = estimatorCheckoutDetails(design, calculation);
      const suggestedPriceText = details.hasEstimatorPrice ? formatCents(details.suggestedPriceCents) : 'Unavailable';
      const note = details.hasEstimatorPrice
        ? 'Suggested retail is a reference only. Checkout price uses the selected transfer size and quantity tiers.'
        : 'Checkout price falls back to the selected size and quantity tier price.';

      return `
        <div class="inknfiber-artwork-estimate inknfiber-dtf-size__checkout-estimate" data-dtf-design-checkout-estimate>
          <dl class="inknfiber-artwork-estimate__rows">
            <div><dt>Tier price</dt><dd>${formatCents(details.basePriceCents)}</dd></div>
            <div><dt>Suggested retail price</dt><dd>${suggestedPriceText}</dd></div>
            <div><dt>Checkout price</dt><dd>${formatCents(details.finalPriceCents)}</dd></div>
          </dl>
          <p class="inknfiber-artwork-estimate__disclaimer">${note}</p>
        </div>
      `;
    }

    function designIsValid(design) {
      return Boolean(design?.file)
        && Number(design.width) > 0
        && Number(design.height) > 0
        && Number(design.qty) > 0;
    }

    function setDesignAspectRatio(design, width = design.width, height = design.height) {
      const ratio = Number(width) / Number(height);
      if (Number.isFinite(ratio) && ratio > 0) design.aspectRatio = ratio;
    }

    function setDesignSizeFromDimension(design, field, value) {
      const ratio = design.aspectRatio || design.width / design.height || 1;
      let width = field === 'height' ? Number(value) * ratio : Number(value);
      let height = field === 'width' ? Number(value) / ratio : Number(value);

      if (!Number.isFinite(width) || width <= 0 || !Number.isFinite(height) || height <= 0) return false;

      if (field === 'width') {
        width = clamp(round(width, 2), minSize, maxSize);
        height = round(width / ratio, 2);

        if (height < minSize) {
          height = minSize;
          width = round(height * ratio, 2);
        } else if (height > maxSize) {
          height = maxSize;
          width = round(height * ratio, 2);
        }
      } else {
        height = clamp(round(height, 2), minSize, maxSize);
        width = round(height * ratio, 2);

        if (width < minSize) {
          width = minSize;
          height = round(width / ratio, 2);
        } else if (width > maxSize) {
          width = maxSize;
          height = round(width / ratio, 2);
        }
      }

      design.width = clamp(round(width, 2), minSize, maxSize);
      design.height = clamp(round(height, 2), minSize, maxSize);
      return true;
    }

    async function runArtworkQualityCheck(design) {
      if (!artworkCheckerEnabled || !artworkChecker || !design?.file) return null;

      try {
        design.artworkQualityResult = await artworkChecker.analyzeFile(design.file, artworkCheckerOptions);
        design.artworkQualityFileKey = artworkFileKey(design.file);
        artworkChecker.reset?.(artworkQualityContainer);
        return design.artworkQualityResult;
      } catch (error) {
        debugLog('artwork quality check failed', {
          fileName: design.file?.name || '',
          error: error?.message || error,
        });
        return null;
      }
    }

    async function confirmArtworkQualityBeforeCart() {
      if (!artworkCheckerEnabled || !artworkChecker?.openModal) return true;
      if (!state.designs.length) return true;

      const results = [];
      for (const design of state.designs) {
        const result = design.artworkQualityFileKey === artworkFileKey(design.file) && design.artworkQualityResult
          ? design.artworkQualityResult
          : await runArtworkQualityCheck(design);
        if (result) results.push(result);
      }

      const allowed = await artworkChecker.openModal(results, artworkCheckerOptions);
      debugLog('artwork quality modal closed', { continueAllowed: allowed, filesChecked: results.length });
      return allowed;
    }

    function popularOptionsMarkup(selectedLabel = '') {
      const options = ['<option value="">Select a popular size</option>'];
      popularSizes.forEach((size, index) => {
        const selected = size.label === selectedLabel ? ' selected' : '';
        options.push(`<option value="${index}"${selected}>${escapeHtml(size.label)}</option>`);
      });
      return options.join('');
    }

    function pricingTableMarkup(design) {
      const activeTier = tierForQty(pricingTiers, design.qty);
      const rows = pricingTiers.map((tier) => {
        const rowPrice = currentCalculation(design, Math.max(design.qty, tier.min), tier);
        const discountLabel = discountLabelForTier(design, tier, rowPrice);
        const active = tier === activeTier ? ' class="is-active"' : '';
        let note = '';

        if (tier === activeTier && activeTier.max !== Infinity) {
          const nextTier = pricingTiers.find((candidate) => candidate.min === activeTier.max + 1);
          if (nextTier) {
            const needed = Math.max(0, nextTier.min - design.qty);
            const nextTierPrice = currentCalculation(design, Math.max(design.qty, nextTier.min), nextTier);
            const nextDiscountLabel = discountLabelForTier(design, nextTier, nextTierPrice);
            note = `<span class="inknfiber-dtf-size__tier-note">${
              needed > 0
                ? escapeHtml(
                    nextDiscountLabel === '-'
                      ? `Add ${needed} of this design to reach the next price tier.`
                      : `Add ${needed} of this design to reach ${nextDiscountLabel} savings.`
                  )
                : escapeHtml(
                    discountLabel === '-'
                      ? 'You are receiving the current price tier.'
                      : `You are receiving ${discountLabel} savings.`
                  )
            }</span>`;
          }
        }

        return `
          <tr${active}>
            <td>${escapeHtml(tierLabel(tier))}${note}</td>
            <td>${money(rowPrice.unitPrice)} each</td>
            <td>${escapeHtml(discountLabel)}</td>
          </tr>
        `;
      });

      return rows.join('');
    }

    function estimatorMoney(value) {
      return artworkEstimator?.formatMoney
        ? artworkEstimator.formatMoney(value, artworkEstimatorConfig?.currency || 'USD')
        : money(value);
    }

    function estimateUsageMarkup(estimate) {
      if (!estimate || !artworkEstimatorConfig?.showInternalCost) return '';
      return `
        <div class="inknfiber-artwork-estimate__internal">
          <h4>Internal production usage</h4>
          <dl>
            <div><dt>Film used</dt><dd>${Number(estimate.filmLengthMeters || 0).toFixed(4)} m</dd></div>
            <div><dt>Film cost</dt><dd>${estimatorMoney(estimate.filmCost)}</dd></div>
            <div><dt>White ink used</dt><dd>${Number(estimate.whiteInkMl || 0).toFixed(4)} ml</dd></div>
            <div><dt>White ink cost</dt><dd>${estimatorMoney(estimate.whiteInkCost)}</dd></div>
            <div><dt>Color ink used</dt><dd>${Number(estimate.colorInkMl || 0).toFixed(4)} ml</dd></div>
            <div><dt>Color ink cost</dt><dd>${estimatorMoney(estimate.colorInkCost)}</dd></div>
            <div><dt>Raw material cost</dt><dd>${estimatorMoney(estimate.rawMaterialCost || estimate.materialCost)}</dd></div>
            <div><dt>Labor / setup</dt><dd>${estimatorMoney(estimate.laborSetupCost || estimate.laborCost)}</dd></div>
            <div><dt>Business cost after waste</dt><dd>${estimatorMoney(estimate.businessCost || estimate.productionCost)}</dd></div>
            <div><dt>Profit multiplier</dt><dd>${Number(estimate.profitMultiplierPercent || 0).toFixed(0)}%</dd></div>
            <div><dt>Complexity multiplier</dt><dd>${Number(estimate.complexityMultiplier || 1).toFixed(2)}x</dd></div>
          </dl>
        </div>
      `;
    }

    function designEstimateMarkup(design) {
      if (!showInternalEstimatorUi()) return '';
      const estimate = design.artworkEstimate || null;
      if (!estimate) {
        return `
          <h3>Internal Artwork Estimate</h3>
          <p class="inknfiber-artwork-estimate__empty">Calculating artwork size, ink coverage, and production usage...</p>
        `;
      }

      const customerRows = [
        `<div><dt>Artwork size</dt><dd>${estimate.widthInches}" x ${estimate.heightInches}"</dd></div>`,
        `<div><dt>Area</dt><dd>${estimate.squareInches} sq in</dd></div>`,
        `<div><dt>Ink coverage</dt><dd>${estimate.coveragePercent}%</dd></div>`,
        `<div><dt>Estimated colors</dt><dd>${estimate.colorCount}</dd></div>`,
        `<div><dt>Color complexity</dt><dd>${escapeHtml(estimate.complexity)}</dd></div>`,
      ].join('');
      return `
        <h3>Internal Artwork Estimate</h3>
        <dl class="inknfiber-artwork-estimate__rows">${customerRows}</dl>
        ${estimateUsageMarkup(estimate)}
      `;
    }

    function renderDesignEstimate(design) {
      if (!designsContainer || !showInternalEstimatorUi()) return;
      const card = Array.from(designsContainer.querySelectorAll('[data-dtf-design-id]'))
        .find((node) => node.dataset.dtfDesignId === design.id);
      const target = card?.querySelector('[data-dtf-design-estimate]');
      if (target) target.innerHTML = designEstimateMarkup(design);
      const checkoutTarget = card?.querySelector('[data-dtf-design-checkout-estimate]');
      if (checkoutTarget && estimatorCheckoutPricingEnabled) checkoutTarget.outerHTML = estimatorCheckoutMarkup(design);
      applyDesignCheckoutDisplay(card, design);
      renderSummary();
    }

    async function refreshDesignEstimateNow(design) {
      if (!design || !artworkEstimator || !artworkEstimatorConfig?.enabled) return null;
      window.clearTimeout(artworkEstimateTimers.get(design.id));

      if (!design.fileDataUrl || !design.width || !design.height) {
        design.artworkEstimate = null;
        design.artworkEstimateKey = '';
        renderDesignEstimate(design);
        return null;
      }

      const key = `${design.fileDataUrl}|${design.width}|${design.height}`;
      if (key === design.artworkEstimateKey && design.artworkEstimate) {
        renderDesignEstimate(design);
        return design.artworkEstimate;
      }

      design.artworkEstimateKey = key;
      const analysis = await artworkEstimator.analyzeImage(design.fileDataUrl);
      if (design.artworkEstimateKey !== key) return design.artworkEstimate || null;
      design.artworkEstimate = artworkEstimator.calculate({
        widthInches: design.width,
        heightInches: design.height,
        analysis,
        config: artworkEstimatorConfig,
      });
      renderDesignEstimate(design);
      return design.artworkEstimate;
    }

    function scheduleDesignEstimate(design) {
      if (!design || !artworkEstimator || !artworkEstimatorConfig?.enabled) return;
      window.clearTimeout(artworkEstimateTimers.get(design.id));
      artworkEstimateTimers.set(design.id, window.setTimeout(async () => {
        await refreshDesignEstimateNow(design);
      }, 250));
    }

    function designCardMarkup(design, index) {
      const calculation = currentCalculation(design);
      const displayPricing = designCheckoutDisplay(design, calculation);
      const activeTier = calculation.tier;
      const popularHidden = design.sizeMode === 'popular' ? '' : ' hidden';
      const customHidden = design.sizeMode === 'popular' ? ' hidden' : '';
      const dimensionsHidden = design.sizeMode === 'popular' ? '' : ' hidden';
      const customActive = design.sizeMode !== 'popular' ? ' is-active' : '';
      const popularActive = design.sizeMode === 'popular' ? ' is-active' : '';
      const selectedSize = `${inchLabel(design.width)}" W x ${inchLabel(design.height)}" H`;
      const measurements = measurementMarkup(design);
      const designLabel = `Layer ${index + 1}`;
      const backgroundActions = aiBackgroundRemoverEnabled
        ? `
          <div class="inknfiber-dtf-size__design-ai-actions">
            <button type="button" data-dtf-design-remove-background${design.backgroundRemovalStatus === 'processing' ? ' disabled' : ''}>
              ${design.backgroundRemovalStatus === 'processing' ? 'Removing background...' : 'Remove Background'}
            </button>
            ${design.backgroundRemoved && design.originalFileDataUrl ? '<button type="button" data-dtf-design-use-original>Use Original</button>' : ''}
            ${design.backgroundRemovalMessage ? `<p class="${design.backgroundRemovalStatus === 'error' ? 'is-error' : ''}">${escapeHtml(design.backgroundRemovalMessage)}</p>` : ''}
          </div>
        `
        : '';

      return `
        <article class="inknfiber-dtf-size__design-card" data-dtf-design-id="${escapeHtml(design.id)}" data-size-mode="${escapeHtml(design.sizeMode)}">
          <div class="inknfiber-dtf-size__design-head">
            <div>
              <p class="inknfiber-eyebrow">Design ${index + 1}</p>
              <h3>${escapeHtml(designLabel)}</h3>
              <p class="inknfiber-dtf-size__design-file-name" title="${escapeHtml(design.fileName)}">${escapeHtml(design.fileName)}</p>
            </div>
            <button type="button" class="inknfiber-dtf-size__remove-design" data-dtf-design-remove aria-label="Remove ${escapeHtml(design.fileName)}">Remove</button>
          </div>
          <div class="inknfiber-dtf-size__design-body">
            <div class="inknfiber-dtf-size__design-preview">
              <div class="inknfiber-dtf-size__measurement-shell">
                <div class="inknfiber-dtf-size__measure inknfiber-dtf-size__measure--width">
                  <span data-dtf-design-width-measure>${escapeHtml(measurements.width)}</span>
                </div>
                <div class="inknfiber-dtf-size__measure-row">
                  <div class="inknfiber-dtf-size__measure inknfiber-dtf-size__measure--height">
                    <span data-dtf-design-height-measure>${escapeHtml(measurements.height)}</span>
                  </div>
                  <div class="inknfiber-dtf-size__artwork-frame">
                    <img src="${escapeHtml(design.fileDataUrl)}" alt="${escapeHtml(`${design.fileName} preview`)}" loading="lazy">
                    <button type="button" class="inknfiber-dtf-size__preview-action inknfiber-dtf-size__preview-action--zoom" data-dtf-design-zoom aria-label="Zoom ${escapeHtml(design.fileName)}">
                      ${previewIcon('zoom')}
                    </button>
                    <button type="button" class="inknfiber-dtf-size__preview-action inknfiber-dtf-size__preview-action--remove" data-dtf-design-remove aria-label="Remove ${escapeHtml(design.fileName)}">
                      ${previewIcon('trash')}
                    </button>
                  </div>
                </div>
              </div>
              ${backgroundActions}
            </div>
            <div class="inknfiber-dtf-size__design-controls">
              <div class="inknfiber-dtf-size__mode-tabs" role="tablist" aria-label="Sizing mode for design ${index + 1}">
                <button type="button" class="${customActive.trim()}" data-dtf-design-mode="custom" role="tab" aria-selected="${design.sizeMode !== 'popular' ? 'true' : 'false'}">
                  Custom Size
                </button>
                <button type="button" class="${popularActive.trim()}" data-dtf-design-mode="popular" role="tab" aria-selected="${design.sizeMode === 'popular' ? 'true' : 'false'}">
                  Popular Size
                </button>
              </div>
              <p class="inknfiber-dtf-size__mode-copy" data-dtf-design-mode-copy>
                ${design.sizeMode === 'popular' ? 'Select a print area for this design.' : 'Width and height stay proportional for this design.'}
              </p>
              <div class="inknfiber-dtf-size__sizing-grid">
                <div class="inknfiber-dtf-size__field inknfiber-dtf-size__popular-field" data-dtf-design-popular-field${popularHidden}>
                  <label>Print Area</label>
                  <select data-dtf-design-popular>${popularOptionsMarkup(design.popularLabel)}</select>
                </div>
                <div class="inknfiber-dtf-size__readout" data-dtf-design-dimensions-field${dimensionsHidden}>
                  <span>Selected size</span>
                  <strong data-dtf-design-dimensions>${escapeHtml(selectedSize)}</strong>
                </div>
                <div class="inknfiber-dtf-size__stepper" data-dtf-design-width-field${customHidden}>
                  <label>Width (inches)</label>
                  <div>
                    <button type="button" data-dtf-design-step="width" data-direction="-1" aria-label="Decrease width">-</button>
                    <input type="number" min="1" max="22" step="0.01" value="${round(design.width, 2).toFixed(2)}" data-dtf-design-width>
                    <button type="button" data-dtf-design-step="width" data-direction="1" aria-label="Increase width">+</button>
                  </div>
                </div>
                <div class="inknfiber-dtf-size__stepper" data-dtf-design-height-field${customHidden}>
                  <label>Height (inches)</label>
                  <div>
                    <button type="button" data-dtf-design-step="height" data-direction="-1" aria-label="Decrease height">-</button>
                    <input type="number" min="1" max="22" step="0.01" value="${round(design.height, 2).toFixed(2)}" data-dtf-design-height>
                    <button type="button" data-dtf-design-step="height" data-direction="1" aria-label="Increase height">+</button>
                  </div>
                </div>
                <div class="inknfiber-dtf-size__field">
                  <label>Qty</label>
                  <input type="number" min="1" step="1" value="${design.qty}" data-dtf-design-qty>
                </div>
                <div class="inknfiber-dtf-size__calculated">
                  <span>Price</span>
                  <strong data-dtf-design-unit>${formatCents(displayPricing.unitCents)}</strong>
                  <small>each</small>
                </div>
                <div class="inknfiber-dtf-size__calculated">
                  <span>Total</span>
                  <strong data-dtf-design-total>${formatCents(displayPricing.totalCents)}</strong>
                </div>
              </div>
              ${showInternalEstimatorUi() ? `
                <div class="inknfiber-artwork-estimate inknfiber-dtf-size__artwork-estimate" data-dtf-design-estimate>
                  ${designEstimateMarkup(design)}
                </div>
              ` : ''}
              ${estimatorCheckoutMarkup(design, calculation)}
              <div class="inknfiber-dtf-size__pricing">
                <div class="inknfiber-dtf-size__pricing-head">
                  <h3>Quantity price tiers</h3>
                  <p data-dtf-design-tier-helper>Active tier: ${escapeHtml(tierLabel(activeTier))}. ${money(calculation.unitPrice)} each for this design.</p>
                </div>
                <table>
                  <thead>
                    <tr>
                      <th>Total Qty</th>
                      <th>Price</th>
                      <th>Discount</th>
                    </tr>
                  </thead>
                  <tbody data-dtf-design-tier-table>${pricingTableMarkup(design)}</tbody>
                </table>
              </div>
            </div>
          </div>
        </article>
      `;
    }

    function renderDesignCards() {
      if (!designsContainer) return;
      if (!state.designs.length) {
        designsContainer.innerHTML = '<div class="inknfiber-dtf-size__designs-empty" data-dtf-size-designs-empty>Upload artwork to create your first design.</div>';
        renderSummary();
        return;
      }

      designsContainer.innerHTML = state.designs.map((design, index) => designCardMarkup(design, index)).join('');
      state.designs.forEach(scheduleDesignEstimate);
      renderSummary();
    }

    function updateDesignCardValues(design) {
      const card = Array.from(designsContainer?.querySelectorAll('[data-dtf-design-id]') || [])
        .find((node) => node.dataset.dtfDesignId === design.id);
      if (!card) {
        renderDesignCards();
        return;
      }

      const calculation = currentCalculation(design);
      const widthInput = card.querySelector('[data-dtf-design-width]');
      const heightInput = card.querySelector('[data-dtf-design-height]');
      const widthField = card.querySelector('[data-dtf-design-width-field]');
      const heightField = card.querySelector('[data-dtf-design-height-field]');
      const qtyInput = card.querySelector('[data-dtf-design-qty]');
      const popularSelect = card.querySelector('[data-dtf-design-popular]');
      const popularField = card.querySelector('[data-dtf-design-popular-field]');
      const dimensionsField = card.querySelector('[data-dtf-design-dimensions-field]');
      const dimensions = card.querySelector('[data-dtf-design-dimensions]');
      const widthMeasure = card.querySelector('[data-dtf-design-width-measure]');
      const heightMeasure = card.querySelector('[data-dtf-design-height-measure]');
      const modeCopy = card.querySelector('[data-dtf-design-mode-copy]');
      const tierHelper = card.querySelector('[data-dtf-design-tier-helper]');
      const tierTable = card.querySelector('[data-dtf-design-tier-table]');
      const checkoutEstimate = card.querySelector('[data-dtf-design-checkout-estimate]');

      card.dataset.sizeMode = design.sizeMode;
      if (widthInput && document.activeElement !== widthInput) widthInput.value = round(design.width, 2).toFixed(2);
      if (heightInput && document.activeElement !== heightInput) heightInput.value = round(design.height, 2).toFixed(2);
      if (qtyInput && document.activeElement !== qtyInput) qtyInput.value = String(design.qty);
      if (popularSelect) popularSelect.value = design.popularLabel ? String(popularSizes.findIndex((size) => size.label === design.popularLabel)) : '';
      if (popularField) popularField.hidden = design.sizeMode !== 'popular';
      if (widthField) widthField.hidden = design.sizeMode === 'popular';
      if (heightField) heightField.hidden = design.sizeMode === 'popular';
      if (dimensionsField) dimensionsField.hidden = design.sizeMode !== 'popular';
      if (dimensions) dimensions.textContent = `${inchLabel(design.width)}" W x ${inchLabel(design.height)}" H`;
      if (widthMeasure) widthMeasure.textContent = `${inchLabel(design.width)}"`;
      if (heightMeasure) heightMeasure.textContent = `${inchLabel(design.height)}"`;
      if (modeCopy) modeCopy.textContent = design.sizeMode === 'popular'
        ? 'Select a print area for this design.'
        : 'Width and height stay proportional for this design.';
      card.querySelectorAll('[data-dtf-design-mode]').forEach((button) => {
        const active = button.dataset.dtfDesignMode === design.sizeMode;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      applyDesignCheckoutDisplay(card, design, calculation);
      if (tierHelper) tierHelper.textContent = `Active tier: ${tierLabel(calculation.tier)}. ${money(calculation.unitPrice)} each for this design.`;
      if (tierTable) tierTable.innerHTML = pricingTableMarkup(design);
      if (checkoutEstimate && estimatorCheckoutPricingEnabled) checkoutEstimate.outerHTML = estimatorCheckoutMarkup(design, calculation);
      scheduleDesignEstimate(design);
      renderSummary();
    }

    function renderSummary() {
      const designCount = state.designs.length;
      const totalQty = state.designs.reduce((sum, design) => sum + parseQty(design.qty, 0), 0);
      const transferTotalCents = state.designs.reduce((sum, design) => {
        return sum + currentCalculation(design).totalCents;
      }, 0);
      const artworkSuggestedCents = state.designs.reduce((sum, design) => {
        return sum + estimatePriceCents(design.artworkEstimate);
      }, 0);
      const estimatedDesignCount = state.designs.filter((design) => design.artworkEstimate).length;
      const finalTotalCents = state.designs.reduce((sum, design) => {
        const calculation = currentCalculation(design);
        return sum + (estimatorCheckoutPricingEnabled ? estimatorCheckoutDetails(design, calculation).finalPriceCents : calculation.totalCents);
      }, 0);

      if (fileLabel) {
        fileLabel.textContent = designCount
          ? `${designCount} design${designCount === 1 ? '' : 's'} uploaded.`
          : 'No designs uploaded.';
      }
      if (summaryDesigns) summaryDesigns.textContent = String(designCount);
      if (summaryQty) summaryQty.textContent = designCount ? String(totalQty) : '-';
      if (summaryTransfer) summaryTransfer.textContent = designCount ? formatCents(transferTotalCents) : '-';
      if (summaryArtwork) {
        summaryArtwork.textContent = !designCount
          ? '-'
          : estimatedDesignCount === designCount
            ? formatCents(artworkSuggestedCents)
            : 'Calculating';
      }
      if (summaryTotal) summaryTotal.textContent = designCount ? formatCents(finalTotalCents) : '-';
      if (summaryList) {
        summaryList.innerHTML = state.designs.map((design, index) => {
          const calculation = currentCalculation(design);
          const checkoutDetails = estimatorCheckoutDetails(design, calculation);
          const designTotalCents = estimatorCheckoutPricingEnabled ? checkoutDetails.finalPriceCents : calculation.totalCents;
          const checkoutUnitCents = design.qty > 0 ? Math.round(designTotalCents / design.qty) : 0;
          const selectedSize = `${inchLabel(design.width)}" W x ${inchLabel(design.height)}" H`;
          const tier = standardDiscountLabel(discountLabelForTier(design, calculation.tier, calculation));
          return `
            <div class="inknfiber-dtf-size__summary-design">
              <strong>Design ${index + 1}</strong>
              <span>${escapeHtml(design.fileName)}</span>
              <span>${escapeHtml(selectedSize)} - Qty ${design.qty} - ${formatCents(designTotalCents)}</span>
              <small>Checkout: ${formatCents(checkoutUnitCents)} each</small>
              <small>${escapeHtml(tier)}</small>
            </div>
          `;
        }).join('');
      }
      if (summaryEstimate && showInternalEstimatorUi()) {
        if (!designCount) {
          summaryEstimate.innerHTML = '<h3>Internal Artwork Analysis</h3><p class="inknfiber-artwork-estimate__empty">Upload artwork to calculate internal production estimates.</p>';
        } else {
          const estimatedDesigns = state.designs.filter((design) => design.artworkEstimate);
          const statusText = estimatedDesigns.length !== state.designs.length
            ? 'Calculating internal artwork analysis...'
            : 'Internal production estimate is calculated per design.';
          summaryEstimate.innerHTML = `<h3>Internal Artwork Analysis</h3><p>${statusText}</p>`;
        }
      }

      const canAdd = hasValidConfig()
        && !state.adding
        && state.designs.length > 0
        && state.designs.every(designIsValid);
      if (addButton) addButton.disabled = !canAdd;
      if (checkoutLink) checkoutLink.disabled = state.adding;

      if (configMessage) {
        const configError = configErrorMessage();
        if (configError) {
          configMessage.textContent = configError;
        } else if (!state.designs.length) {
          configMessage.textContent = 'Upload at least one artwork to add DTF transfers to cart.';
        } else {
          configMessage.textContent = '';
        }
      }
    }

    function clearPopularSelectionForCustomSize(design) {
      const matchingSize = findMatchingPopular(popularSizes, design.width, design.height);
      if (!matchingSize || matchingSize.label !== design.popularLabel) {
        design.popularLabel = '';
      }
    }

    async function createDesignFromFile(file, restoredData = {}) {
      if (!allowedTypes.includes(file.type)) {
        throw new Error(`${file.name}: Please upload a PNG, JPG, JPEG, or WEBP image.`);
      }
      if (file.size > maxFileBytes) {
        throw new Error(`${file.name}: Artwork must be 25MB or smaller.`);
      }

      const fileDataUrl = await fileToDataUrl(file);
      const design = {
        id: safeId('dtf-transfer'),
        file,
        fileName: file.name,
        fileDataUrl,
        width: parsePositiveNumber(restoredData.width, pricingConfig.customSizeReferenceWidth),
        height: parsePositiveNumber(restoredData.height, pricingConfig.customSizeReferenceHeight),
        qty: parseQty(restoredData.quantity, 1),
        popularLabel: String(restoredData.popular_size_label || ''),
        sizeMode: restoredData.popular_size_label ? 'popular' : 'custom',
        aspectRatio: pricingConfig.customSizeReferenceWidth / pricingConfig.customSizeReferenceHeight,
        naturalWidth: 0,
        naturalHeight: 0,
        artworkQualityResult: null,
        artworkQualityFileKey: '',
        originalUploadFile: restoredData.originalUploadFile || file,
        originalFileDataUrl: restoredData.originalFileDataUrl || fileDataUrl,
        originalDesignKey: String(restoredData.originalDesignKey || restoredData.original_design_key || ''),
        originalDesignUrl: String(restoredData.originalDesignUrl || ''),
        backgroundRemoved: Boolean(restoredData.backgroundRemoved),
        backgroundRemovedDesignKey: String(restoredData.backgroundRemovedDesignKey || ''),
        backgroundRemovedImageUrl: String(restoredData.backgroundRemovedImageUrl || ''),
        backgroundRemovalStatus: '',
        backgroundRemovalMessage: '',
        aiGenerated: Boolean(restoredData.aiGenerated),
        aiPrompt: String(restoredData.aiPrompt || restoredData.ai_enhanced_prompt || ''),
        aiOriginalPrompt: String(restoredData.aiOriginalPrompt || restoredData.ai_original_prompt || ''),
        aiEnhancedPrompt: String(restoredData.aiEnhancedPrompt || restoredData.ai_enhanced_prompt || restoredData.aiPrompt || ''),
        aiPromptStyle: String(restoredData.aiPromptStyle || restoredData.ai_prompt_style || ''),
        aiPromptOutputType: String(restoredData.aiPromptOutputType || restoredData.ai_prompt_output_type || ''),
        aiPromptIncludeText: Boolean(restoredData.aiPromptIncludeText || restoredData.ai_prompt_include_text),
        aiImageKey: String(restoredData.aiImageKey || restoredData.ai_image_key || ''),
        aiImageUrl: String(restoredData.aiImageUrl || ''),
      };

      try {
        const artworkImage = await imageFromSrc(fileDataUrl);
        design.naturalWidth = artworkImage.naturalWidth || 0;
        design.naturalHeight = artworkImage.naturalHeight || 0;
        if (design.naturalWidth && design.naturalHeight && !restoredData.width && !restoredData.height && !design.popularLabel) {
          setDesignAspectRatio(design, design.naturalWidth, design.naturalHeight);
          setDesignSizeFromDimension(design, 'width', design.width);
        } else {
          setDesignAspectRatio(design, design.width, design.height);
        }
      } catch (error) {
        debugLog('artwork dimensions unavailable', { fileName: file.name, error: error?.message || error });
        setDesignAspectRatio(design, design.width, design.height);
      }

      if (design.popularLabel) {
        const popularSize = popularSizes.find((size) => size.label === design.popularLabel);
        if (popularSize) {
          design.width = popularSize.width;
          design.height = popularSize.height;
          setDesignAspectRatio(design, popularSize.width, popularSize.height);
        }
      }

      await runArtworkQualityCheck(design);
      return design;
    }

    async function handleFiles(files) {
      const selectedFiles = Array.from(files || []);
      if (!selectedFiles.length) return;

      const added = [];
      const errors = [];
      setStatus(`Adding ${selectedFiles.length} design${selectedFiles.length === 1 ? '' : 's'}...`, 'info');

      for (const file of selectedFiles) {
        try {
          const design = await createDesignFromFile(file);
          added.push(design);
        } catch (error) {
          errors.push(error?.message || `Unable to add ${file?.name || 'this file'}.`);
        }
      }

      if (added.length) {
        state.designs.push(...added);
        renderDesignCards();
      }

      if (errors.length) {
        setStatus(errors.join(' '), 'error');
      } else {
        setStatus(`${added.length} design${added.length === 1 ? '' : 's'} added.`, 'success');
      }
    }

    function reorderRestoreEndpoint() {
      const fallbackEndpoint = backendEndpoint('/dtf-transfer-by-size-upload');
      const origin = new URL(uploadEndpoint || fallbackEndpoint, window.location.origin).origin;
      const url = new URL('/reorder/restore', origin);
      url.searchParams.set('reorder_id', reorderId || '');
      url.searchParams.set('token', reorderToken || '');
      return url.toString();
    }

    async function fileFromRestoreUrl(url, fileName = 'restored-transfer.png') {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Saved artwork request failed with ${response.status}`);
      const blob = await response.blob();
      const type = blob.type || 'image/png';
      const extension = type === 'image/webp' ? '.webp' : type === 'image/jpeg' ? '.jpg' : '.png';
      const safeName = /\.[a-z0-9]+$/i.test(fileName) ? fileName : `${fileName}${extension}`;
      return new File([blob], safeName, { type });
    }

    async function uploadTempOriginalFile(file, design) {
      if (!file || design.originalDesignKey) return design.originalDesignKey || '';

      const formData = new FormData();
      formData.set('design_id', design.id || state.orderId);
      formData.set('file_name', file.name || 'dtf-transfer-artwork.png');
      formData.set('image', file);

      const response = await fetch(customDesignFileUploadEndpoint, {
        method: 'POST',
        body: formData,
      });
      const body = await response.json().catch(() => null);

      if (!response.ok || !body?.ok) {
        debugLog('temporary original upload failed', {
          fileName: file.name,
          error: body?.error || response.status,
        });
        return '';
      }

      design.originalDesignKey = body.original_design_key || body.r2_key || '';
      design.originalDesignUrl = absoluteWorkerUrl(body.preview_url || `/custom-design-preview?key=${encodeURIComponent(design.originalDesignKey)}`);
      return design.originalDesignKey;
    }

    async function requestBackgroundRemoval(file, design) {
      if (!aiBackgroundRemoverEnabled) throw new Error('AI background removal is not enabled.');
      if (!file) throw new Error('Re-upload this artwork before removing the background.');

      const cacheKey = aiFileCacheKey(file, aiPerformanceMode, 'dtf-transfer-background');
      const cached = aiSessionCache.backgrounds.get(cacheKey);
      if (cached?.file && cached?.imageKey) {
        setStatus(`Using previous background removal for ${file.name || 'design'}.`, 'info');
        return {
          file: cached.file,
          fileName: cached.fileName || cached.file.name || 'background-removed-artwork.png',
          mimeType: cached.mimeType || cached.file.type || 'image/png',
          imageKey: cached.imageKey || '',
          imageUrl: cached.imageUrl || '',
        };
      }

      setStatus('Preparing image...', 'info');
      const optimizedImage = await optimizeImageForAi(file, aiBackgroundMaxDimension);
      setStatus('Sending to AI...', 'info');

      const formData = new FormData();
      formData.set('design_id', design.id || state.orderId);
      formData.set('file_name', file.name || 'dtf-transfer-artwork.png');
      formData.set('performance_mode', aiPerformanceMode);
      formData.set('optimized_for_ai', optimizedImage.optimized ? 'true' : 'false');
      formData.set('image', optimizedImage.file || file);

      const response = await fetch(aiBackgroundRemoveEndpoint, {
        method: 'POST',
        body: formData,
      });
      const body = await response.json().catch(() => null);

      if (!response.ok || !body?.ok || !body?.image_key) {
        throw new Error(friendlyAiError(body, 'Background removal failed. Your original file was kept.'));
      }

      setStatus('Almost done...', 'info');
      const imageUrl = absoluteWorkerUrl(body.image_url || `/custom-design-preview?key=${encodeURIComponent(body.image_key || '')}`);
      const imageResponse = await fetch(imageUrl);
      if (!imageResponse.ok) throw new Error(`Background-removed image request failed with ${imageResponse.status}${supportRequestSuffix(body)}`);

      const blob = await imageResponse.blob();
      const mimeType = body.mime_type || blob.type || 'image/png';
      if (!allowedTypes.includes(mimeType)) {
        throw new Error(`Background removal returned an unsupported image type. Your original file was kept.${supportRequestSuffix(body)}`);
      }

      const fileName = body.file_name || `${(file.name || 'dtf-transfer-artwork').replace(/\.[a-z0-9]+$/i, '')}-background-removed.png`;
      const result = {
        file: new File([blob], fileName, { type: mimeType }),
        fileName,
        mimeType,
        imageKey: body.image_key || '',
        imageUrl,
      };
      aiSessionCache.backgrounds.set(cacheKey, result);
      return result;
    }

    async function removeBackgroundForDesign(design) {
      if (!design || design.backgroundRemovalStatus === 'processing') return;
      const sourceFile = design.originalUploadFile || design.file;
      if (!sourceFile) {
        design.backgroundRemovalStatus = 'error';
        design.backgroundRemovalMessage = 'Re-upload this artwork before removing the background.';
        renderDesignCards();
        return;
      }

      design.backgroundRemovalStatus = 'processing';
      design.backgroundRemovalMessage = 'Preparing image...';
      setStatus('Preparing image...', 'info');
      renderDesignCards();

      try {
        design.originalUploadFile = design.originalUploadFile || design.file;
        design.originalFileDataUrl = design.originalFileDataUrl || design.fileDataUrl;
        await uploadTempOriginalFile(design.originalUploadFile, design);

        const result = await requestBackgroundRemoval(sourceFile, design);
        design.file = result.file;
        design.fileName = result.fileName;
        design.fileDataUrl = await fileToDataUrl(result.file);
        design.backgroundRemoved = true;
        design.backgroundRemovedDesignKey = result.imageKey;
        design.backgroundRemovedImageUrl = result.imageUrl;
        design.backgroundRemovalStatus = 'done';
        design.backgroundRemovalMessage = 'Background removed. Original upload is preserved.';
        design.artworkQualityResult = null;
        design.artworkQualityFileKey = '';
        await runArtworkQualityCheck(design);
        setStatus('Background removed for that design.', 'success');
      } catch (error) {
        design.backgroundRemovalStatus = 'error';
        design.backgroundRemovalMessage = error?.message || 'Background removal failed. Your original file was kept.';
        setStatus(design.backgroundRemovalMessage, 'error');
      } finally {
        renderDesignCards();
      }
    }

    async function useOriginalForDesign(design) {
      if (!design?.originalFileDataUrl) return;
      design.fileDataUrl = design.originalFileDataUrl;
      if (design.originalUploadFile) {
        design.file = design.originalUploadFile;
        design.fileName = design.originalUploadFile.name || design.fileName;
      }
      design.backgroundRemoved = false;
      design.backgroundRemovalStatus = '';
      design.backgroundRemovalMessage = 'Using original artwork.';
      design.artworkQualityResult = null;
      design.artworkQualityFileKey = '';
      await runArtworkQualityCheck(design);
      setStatus('Original artwork restored for that design.', 'info');
      renderDesignCards();
    }

    function setAiStatus(message = '', tone = 'info') {
      if (!aiStatus) return;
      aiStatus.hidden = !message;
      aiStatus.textContent = message || '';
      aiStatus.dataset.tone = tone;
    }

    function syncAiPreview() {
      if (!aiPreview || !aiPreviewImage || !aiAddButton) return;
      const hasDesign = Boolean(generatedAiDesign?.imageUrl);
      aiPreview.hidden = !hasDesign;
      aiPreviewImage.src = hasDesign ? generatedAiDesign.imageUrl : '';
      aiAddButton.disabled = !hasDesign;
    }

    function getAiPromptOptions() {
      return {
        style: aiDesignStyleSelect?.value || 'Clean Vector',
        outputType: aiOutputTypeSelect?.value || 'T-Shirt Design',
        includeText: Boolean(aiIncludeTextInput?.checked),
      };
    }

    function syncAiPromptWarning(message = '') {
      if (!aiPromptWarning) return;
      const warning = message || aiPromptHelper.getAiPromptSafetyWarning(aiPromptInput?.value || '');
      aiPromptWarning.textContent = warning;
      aiPromptWarning.hidden = !warning;
    }

    async function generateAiDesign() {
      if (!aiPhotoshootEnabled || !aiPromptInput || !aiGenerateButton) return;
      const promptRequest = aiPromptHelper.buildAiDesignPrompt(aiPromptInput.value, getAiPromptOptions());
      syncAiPromptWarning(promptRequest.warning);
      if (!promptRequest.originalPrompt) {
        setAiStatus('Enter a prompt before generating a design.', 'error');
        return;
      }
      const prompt = promptRequest.enhancedPrompt;

      generatedAiDesign = null;
      syncAiPreview();
      const cacheKey = aiPromptCacheKey(prompt, aiPerformanceMode, 'dtf-transfer-ai-design');
      const cachedDesign = aiSessionCache.designs.get(cacheKey);
      if (cachedDesign?.imageKey && cachedDesign?.imageUrl) {
        generatedAiDesign = { ...cachedDesign, ...promptRequest, prompt };
        syncAiPreview();
        setAiStatus('Using previous AI design for this prompt and mode.', 'success');
        return;
      }

      aiGenerateButton.disabled = true;
      aiGenerateButton.textContent = 'Generating...';
      setAiStatus('Sending to AI...', 'info');

      try {
        const response = await fetch(aiDesignGenerateEndpoint, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            prompt,
            design_id: state.orderId,
            transparent_background: true,
            performance_mode: aiPerformanceMode,
          }),
        });
        setAiStatus('Generating design...', 'info');
        const body = await response.json().catch(() => null);

        if (!response.ok || !body?.ok || !body?.image_key) {
          throw new Error(friendlyAiError(body, 'AI generation failed. Please try again.'));
        }

        generatedAiDesign = {
          imageKey: body.image_key,
          imageUrl: absoluteWorkerUrl(body.image_url || `/custom-design-preview?key=${encodeURIComponent(body.image_key || '')}`),
          fileName: body.file_name || 'ai-generated-dtf-design.png',
          mimeType: body.mime_type || 'image/png',
          prompt,
          originalPrompt: promptRequest.originalPrompt,
          enhancedPrompt: prompt,
          aiPromptStyle: promptRequest.style,
          aiPromptOutputType: promptRequest.outputType,
          aiPromptIncludeText: promptRequest.includeText,
        };
        aiSessionCache.designs.set(cacheKey, { ...generatedAiDesign });
        syncAiPreview();
        setAiStatus('Almost done...', 'info');
        window.setTimeout(() => {
          if (generatedAiDesign?.imageKey === body.image_key) {
            setAiStatus('AI design generated. Review it, then add it as a transfer design.', 'success');
          }
        }, 250);
      } catch (error) {
        setAiStatus(error?.message || 'AI generation failed. Please try again.', 'error');
      } finally {
        aiGenerateButton.disabled = false;
        aiGenerateButton.textContent = 'Generate AI Design';
      }
    }

    async function addGeneratedAiDesign() {
      if (!aiPhotoshootEnabled || !generatedAiDesign?.imageUrl || !generatedAiDesign?.imageKey) return;
      if (aiAddButton) aiAddButton.disabled = true;
      setAiStatus('Adding AI design...', 'info');

      try {
        const response = await fetch(generatedAiDesign.imageUrl);
        if (!response.ok) throw new Error(`Generated image request failed with ${response.status}`);
        const blob = await response.blob();
        const mimeType = generatedAiDesign.mimeType || blob.type || 'image/png';
        if (!allowedTypes.includes(mimeType)) throw new Error('Generated image is not a supported image type.');

        const file = new File([blob], generatedAiDesign.fileName || 'ai-generated-dtf-design.png', { type: mimeType });
        const design = await createDesignFromFile(file, {
          aiGenerated: true,
          aiPrompt: generatedAiDesign.prompt,
          aiOriginalPrompt: generatedAiDesign.originalPrompt || '',
          aiEnhancedPrompt: generatedAiDesign.enhancedPrompt || generatedAiDesign.prompt || '',
          aiPromptStyle: generatedAiDesign.aiPromptStyle || 'Clean Vector',
          aiPromptOutputType: generatedAiDesign.aiPromptOutputType || 'T-Shirt Design',
          aiPromptIncludeText: Boolean(generatedAiDesign.aiPromptIncludeText),
          aiImageKey: generatedAiDesign.imageKey,
          aiImageUrl: generatedAiDesign.imageUrl,
          originalDesignKey: generatedAiDesign.imageKey,
        });
        state.designs.push(design);
        renderDesignCards();
        setAiStatus('AI design added as a transfer.', 'success');
        setStatus('AI design added as a DTF transfer design.', 'success');
      } catch (error) {
        setAiStatus(error?.message || 'Unable to add AI design.', 'error');
      } finally {
        if (aiAddButton) aiAddButton.disabled = false;
      }
    }

    async function restoreFromReorderLink() {
      if (!reorderId || !reorderToken) return;

      setStatus('Loading reorder details...', 'info');
      try {
        const response = await fetch(reorderRestoreEndpoint(), { headers: { Accept: 'application/json' } });
        const data = await response.json().catch(() => null);
        if (!response.ok || !data?.ok) throw new Error(data?.error || 'Unable to load this reorder.');
        if (data.source_type !== 'dtf_transfer_by_size') throw new Error('This reorder link is not for DTF Transfers By Size.');
        if (!data.image_url) throw new Error('This reorder does not include artwork to restore.');

        const restoredFile = await fileFromRestoreUrl(data.image_url, data.uploaded_file_name || 'restored-transfer.png');
        const restoredDesign = await createDesignFromFile(restoredFile, data);
        restoredDesign.id = safeId('dtf-transfer');
        state.designs.push(restoredDesign);
        renderDesignCards();

        const warnings = Array.isArray(data.warnings) ? data.warnings.filter(Boolean) : [];
        setStatus(
          warnings.length
            ? `Reorder loaded with notes: ${warnings.join(' ')}`
            : 'Reorder loaded. Review your transfer before adding to cart.',
          warnings.length ? 'warning' : 'success'
        );
      } catch (error) {
        debugLog('reorder restore failed', { reorderId, error: error?.message || error });
        setStatus(error?.message || 'This reorder could not be restored. Please reupload your artwork or contact InkNfiber.', 'error');
        renderSummary();
      }
    }

    async function buildPreviewBlob(design) {
      if (!design.fileDataUrl) return null;
      const image = await imageFromSrc(design.fileDataUrl);
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');

      canvas.width = 1200;
      canvas.height = 1200;
      if (!context) return null;

      context.fillStyle = '#f7f7f7';
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.strokeStyle = '#dddddd';
      context.lineWidth = 2;
      context.strokeRect(32, 32, canvas.width - 64, canvas.height - 64);

      const maxW = canvas.width - 160;
      const maxH = canvas.height - 210;
      const scale = Math.min(maxW / image.naturalWidth, maxH / image.naturalHeight, 1);
      const drawW = image.naturalWidth * scale;
      const drawH = image.naturalHeight * scale;
      const drawX = (canvas.width - drawW) / 2;
      const drawY = (canvas.height - drawH) / 2 - 20;

      context.drawImage(image, drawX, drawY, drawW, drawH);
      context.fillStyle = '#111111';
      context.font = '700 34px Arial, sans-serif';
      context.textAlign = 'center';
      context.fillText(`${inchLabel(design.width)}" W x ${inchLabel(design.height)}" H - Qty ${design.qty}`, canvas.width / 2, canvas.height - 70);

      return canvasToBlob(canvas);
    }

    function orderJson(design, index) {
      const calculation = currentCalculation(design);
      const transferSizeLabel = `${inchLabel(design.width)}" W x ${inchLabel(design.height)}" H`;
      return {
        order_id: state.orderId,
        design_index: index + 1,
        design_count: state.designs.length,
        transfer_id: design.id,
        transfer_type: 'By Size',
        uploaded_file_name: design.file?.name || design.fileName || '',
        width_in: design.width,
        height_in: design.height,
        transfer_size_label: transferSizeLabel,
        quantity_ordered: design.qty,
        size_mode: design.sizeMode,
        popular_size_label: design.popularLabel || '',
        pricing: {
          unit_price: calculation.unitPrice,
          total: calculation.total,
          unit_cents: calculation.unitCents,
          total_cents: calculation.totalCents,
          tier: tierLabel(calculation.tier),
          discount: discountLabelForTier(design, calculation.tier, calculation),
          rate_per_sq_in: calculation.tier.ratePerSqIn,
          min_unit_price: calculation.tier.minUnitPrice,
          price_override: selectedPopularSize(design)?.priceOverride || 0,
        },
        artwork: {
          original_design_key: design.originalDesignKey || '',
          background_removed_design_key: design.backgroundRemovedDesignKey || '',
          ai_generated: Boolean(design.aiGenerated),
          ai_image_key: design.aiImageKey || '',
          ai_original_prompt: design.aiOriginalPrompt || '',
          ai_enhanced_prompt: design.aiEnhancedPrompt || design.aiPrompt || '',
          ai_prompt_style: design.aiPromptStyle || '',
          ai_prompt_output_type: design.aiPromptOutputType || '',
          ai_prompt_include_text: Boolean(design.aiPromptIncludeText),
        },
        created_at: new Date().toISOString(),
      };
    }

    async function uploadAssets(design, previewBlob, index) {
      if (!r2Enabled) return {};
      if (!uploadEndpoint) throw new Error('DTF transfer upload endpoint is not configured.');

      const formData = new FormData();
      formData.set('transfer_id', design.id);
      formData.set('original_design', design.file, design.file.name);
      formData.set('order_json', JSON.stringify(orderJson(design, index)));
      if (previewBlob) formData.set('preview_image', previewBlob, 'dtf-transfer-preview.png');

      const response = await fetch(uploadEndpoint, {
        method: 'POST',
        body: formData,
      });
      const body = await response.json().catch(() => null);

      if (!response.ok || !body?.ok) {
        throw new Error(body?.error || `Unable to upload ${design.fileName}.`);
      }

      debugLog('upload complete', { designId: design.id, fileName: design.fileName, body });
      return body;
    }

    function persistTransfer(design, uploadResult = {}, index = 0) {
      if (!design.fileDataUrl) return;
      const payload = {
        ...orderJson(design, index),
        file_data_url: design.fileDataUrl,
        upload_result: uploadResult,
      };

      try {
        localStorage.setItem(`${storagePrefix}${design.id}`, JSON.stringify(payload));
      } catch (error) {
        debugLog('localStorage persist failed', { designId: design.id, error: error?.message || error });
      }
    }

    function canSubmitDesigns() {
      return !state.adding && hasValidConfig() && state.designs.length && state.designs.every(designIsValid);
    }

    function setAdding(isAdding, label = 'Add DTF Transfers to Cart', checkoutLabel = 'Proceed to Checkout') {
      state.adding = isAdding;
      if (addButton) {
        addButton.disabled = !canSubmitDesigns();
        addButton.textContent = label;
      }
      if (checkoutLink) {
        checkoutLink.disabled = isAdding;
        checkoutLink.textContent = checkoutLabel;
      }
    }

    function buildCartItems(uploadResults) {
      const items = [];

      state.designs.forEach((design, index) => {
        const calculation = currentCalculation(design);
        const checkoutDetails = estimatorCheckoutDetails(design, calculation);
        const checkoutTotalCents = calculation.totalCents;
        const checkoutUnitCents = design.qty > 0 ? Math.round(checkoutTotalCents / design.qty) : 0;
        const uploadResult = uploadResults[index] || {};
        const unitText = `${money(calculation.unitPrice)} each`;
        const totalText = money(calculation.total);
        const checkoutUnitText = `${formatCents(checkoutUnitCents)} each`;
        const checkoutTotalText = formatCents(checkoutTotalCents);
        const designNumber = `Design ${index + 1} of ${state.designs.length}`;
        const properties = {
          'DTF Transfer ID': design.id,
          'DTF Order ID': state.orderId,
          'DTF Design': designNumber,
          'DTF Transfer Type': 'By Size',
          'Uploaded file name': design.file.name,
          'Width': `${inchLabel(design.width)}"`,
          'Height': `${inchLabel(design.height)}"`,
          'Transfer Size': `${inchLabel(design.width)}" W x ${inchLabel(design.height)}" H`,
          'Quantity ordered': String(design.qty),
          'Tier unit price': unitText,
          'Tier price': totalText,
          'Suggested retail reference': checkoutDetails.hasEstimatorPrice ? formatCents(checkoutDetails.suggestedPriceCents) : 'Unavailable',
          'Checkout unit price': checkoutUnitText,
          'Checkout price': checkoutTotalText,
          'Unit price': checkoutUnitText,
          'Design total': checkoutTotalText,
          'Total': checkoutTotalText,
          'Discount tier': standardDiscountLabel(discountLabelForTier(design, calculation.tier, calculation)),
          'Pricing mode': design.sizeMode === 'popular' ? 'Popular Size' : 'Custom Size',
          'Popular size': design.popularLabel || '',
          'Selected print area': design.popularLabel || '',
          'Artwork upload': uploadResult.original_design_key ? 'uploaded' : 'not uploaded',
          '_dtf_transfer_original_design_key': uploadResult.original_design_key || '',
          '_dtf_transfer_original_uploaded_design_key': design.originalDesignKey || '',
          '_dtf_transfer_background_removed_design_key': design.backgroundRemovedDesignKey || '',
          '_dtf_transfer_ai_design_key': design.aiImageKey || '',
          '_dtf_transfer_ai_prompt': design.aiPrompt || '',
          '_dtf_transfer_ai_original_prompt': design.aiOriginalPrompt || '',
          '_dtf_transfer_ai_enhanced_prompt': design.aiEnhancedPrompt || design.aiPrompt || '',
          '_dtf_transfer_ai_prompt_style': design.aiPromptStyle || '',
          '_dtf_transfer_ai_prompt_output_type': design.aiPromptOutputType || '',
          '_dtf_transfer_ai_prompt_include_text': design.aiPromptIncludeText ? 'true' : 'false',
          '_dtf_transfer_preview_key': uploadResult.preview_key || '',
          '_dtf_transfer_order_json_key': uploadResult.order_json_key || '',
          '_inknfiber_transfer_id': design.id,
          '_inknfiber_order_id': state.orderId,
          '_inknfiber_design_index': String(index + 1),
          '_inknfiber_transfer_tier_total_cents': String(calculation.totalCents),
          '_inknfiber_transfer_total_cents': String(checkoutTotalCents),
          '_estimator_checkout_pricing_enabled': estimatorCheckoutPricingEnabled ? 'true' : 'false',
          '_estimator_base_price': formatCents(checkoutDetails.basePriceCents),
          '_estimator_suggested_retail_price': formatCents(checkoutDetails.suggestedPriceCents),
          '_estimator_final_checkout_price': formatCents(checkoutDetails.finalPriceCents),
          '_estimator_adjustment_amount': formatCents(checkoutDetails.adjustmentCents),
          '_estimator_pricing_mode': estimatorCheckoutPriceMode,
          '_estimator_pricing_model': estimatorCheckoutPriceMode,
        };
        if (artworkEstimator && design.artworkEstimate) {
          Object.assign(properties, artworkEstimator.metadata(design.artworkEstimate));
          properties['_artwork_estimate_area_sq_in'] = String(design.artworkEstimate.squareInches);
        }
        const feeProperties = {
          '_inknfiber_fee_item': 'true',
          '_inknfiber_fee_type': 'dtf_transfer_by_size_price',
          '_inknfiber_parent_transfer_id': design.id,
          '_inknfiber_order_id': state.orderId,
          '_inknfiber_design_index': String(index + 1),
          '_inknfiber_tier_total_cents': String(calculation.totalCents),
          '_inknfiber_total_cents': String(checkoutTotalCents),
          '_base_price': formatCents(checkoutDetails.basePriceCents),
          '_suggested_price': checkoutDetails.hasEstimatorPrice ? formatCents(checkoutDetails.suggestedPriceCents) : 'Unavailable',
          '_final_price': formatCents(checkoutDetails.finalPriceCents),
          '_estimator_pricing_model': estimatorCheckoutPriceMode,
          '_hidden_helper_line': 'true',
        };

        items.push({
          id: Number(productVariantId),
          quantity: 1,
          properties,
        });
        if (checkoutTotalCents > 0) {
          items.push({
            id: Number(priceVariantId),
            quantity: checkoutTotalCents,
            properties: feeProperties,
          });
        }
      });

      return items;
    }

    async function addToCart(options = {}) {
      const redirectToCheckout = options.redirectToCheckout === true;
      if (state.adding) return false;
      const configError = configErrorMessage();
      if (configError) {
        setStatus(configError, 'error');
        return false;
      }
      if (!state.designs.length) {
        setStatus('Upload at least one artwork before adding to cart.', 'error');
        return false;
      }

      const invalidIndex = state.designs.findIndex((design) => !designIsValid(design));
      if (invalidIndex >= 0) {
        setStatus(`Design ${invalidIndex + 1} needs a valid size and quantity.`, 'error');
        return false;
      }

      for (const [index, design] of state.designs.entries()) {
        const calculation = currentCalculation(design);
        const helperError = validatePriceAdjustmentProductForAmount(calculation.totalCents, `Design ${index + 1}`);
        if (helperError) {
          setStatus(helperError, 'error');
          return false;
        }
      }

      debugLog('add to cart clicked - running artwork quality gate', {
        fileNames: state.designs.map((design) => design.fileName),
      });
      const qualityApproved = await confirmArtworkQualityBeforeCart();
      if (!qualityApproved) {
        setStatus('Review or reupload artwork before adding DTF transfers to cart.', 'error');
        return false;
      }

      if (estimatorCheckoutPricingEnabled && artworkEstimator && artworkEstimatorConfig?.enabled) {
        setStatus('Calculating checkout prices...', 'info');
        for (const design of state.designs) {
          await refreshDesignEstimateNow(design);
          const adjustmentError = validateEstimatorAdjustmentProduct(estimatorCheckoutDetails(design));
          if (adjustmentError) {
            setStatus(adjustmentError, 'error');
            return false;
          }
        }
      }

      setAdding(
        true,
        redirectToCheckout ? 'Preparing checkout...' : 'Preparing transfers...',
        redirectToCheckout ? 'Preparing checkout...' : 'Proceed to Checkout'
      );
      setStatus('Preparing artwork previews...', 'info');

      try {
        const uploadResults = [];
        for (const [index, design] of state.designs.entries()) {
          const stepLabel = r2Enabled ? `Uploading design ${index + 1}...` : `Preparing design ${index + 1}...`;
          setAdding(true, stepLabel, redirectToCheckout ? stepLabel : 'Proceed to Checkout');
          const previewBlob = await buildPreviewBlob(design);
          const uploadResult = await uploadAssets(design, previewBlob, index);
          uploadResults.push(uploadResult);
          persistTransfer(design, uploadResult, index);
        }

        setAdding(true, redirectToCheckout ? 'Adding and opening checkout...' : 'Adding...', redirectToCheckout ? 'Opening checkout...' : 'Proceed to Checkout');
        const items = buildCartItems(uploadResults);
        const cartPayloadError = validateCartItems(items);
        if (cartPayloadError) throw new Error(cartPayloadError);
        debugLog('cart add payload prepared', {
          lineCount: items.length,
          itemSummary: items.map((item) => ({
            id: item.id,
            quantity: item.quantity,
            feeType: item.properties?._inknfiber_fee_type || '',
            transferId: item.properties?._inknfiber_parent_transfer_id || item.properties?.['DTF Transfer ID'] || '',
          })),
        });

        if (window.InkNfiberCart?.addItems) {
          await window.InkNfiberCart.addItems(items, {
            source: 'inknfiber-dtf-transfers-by-size',
            errorMessage: 'Unable to add DTF transfers to cart.',
          });
        } else {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ items }),
          });
          const body = await response.json().catch(() => null);

          if (!response.ok) {
            const error = new Error(body?.description || body?.message || 'Unable to add DTF transfers to cart.');
            error.response = body;
            throw error;
          }

          const cartResponse = await fetch('/cart.js');
          const cart = await cartResponse.json();
          dispatchCartUpdate(cart);
        }

        if (viewCartLink) viewCartLink.hidden = false;
        setStatus(`${state.designs.length} DTF design${state.designs.length === 1 ? '' : 's'} added to cart.`, 'success');
        if (redirectToCheckout) {
          window.location.href = '/checkout';
        }
        return true;
      } catch (error) {
        console.error('[InkNfiber DTF transfers by size] Add to cart failed:', error);
        setStatus(cartApiErrorMessage(error), 'error');
        return false;
      } finally {
        setAdding(false);
        renderSummary();
      }
    }

    function designFromEventTarget(target) {
      const card = target.closest('[data-dtf-design-id]');
      if (!card) return { card: null, design: null };
      return {
        card,
        design: state.designs.find((item) => item.id === card.dataset.dtfDesignId) || null,
      };
    }

    uploadInput?.addEventListener('change', (event) => {
      handleFiles(event.target.files).catch((error) => setStatus(error?.message || 'Unable to upload artwork.', 'error'));
      event.target.value = '';
    });

    addDesignButton?.addEventListener('click', () => uploadInput?.click());

    designsContainer?.addEventListener('click', (event) => {
      const target = event.target;
      const { design } = designFromEventTarget(target);
      if (!design) return;

      if (target.closest('[data-dtf-design-remove]')) {
        state.designs = state.designs.filter((item) => item.id !== design.id);
        setStatus(state.designs.length ? 'Design removed.' : '', 'info');
        renderDesignCards();
        return;
      }

      if (target.closest('[data-dtf-design-remove-background]')) {
        removeBackgroundForDesign(design)
          .catch((error) => setStatus(error?.message || 'Background removal failed. Your original file was kept.', 'error'));
        return;
      }

      if (target.closest('[data-dtf-design-use-original]')) {
        useOriginalForDesign(design)
          .catch((error) => setStatus(error?.message || 'Unable to restore the original artwork.', 'error'));
        return;
      }

      if (target.closest('[data-dtf-design-zoom]')) {
        openZoomModal(design);
        return;
      }

      const modeButton = target.closest('[data-dtf-design-mode]');
      if (modeButton) {
        design.sizeMode = modeButton.dataset.dtfDesignMode === 'popular' ? 'popular' : 'custom';
        if (design.sizeMode === 'custom') {
          design.popularLabel = '';
        }
        renderDesignCards();
        return;
      }

      const stepButton = target.closest('[data-dtf-design-step]');
      if (stepButton) {
        const field = stepButton.dataset.dtfDesignStep;
        const direction = Number(stepButton.dataset.direction || 1);
        const step = 0.25 * direction;

        if (field === 'width') setDesignSizeFromDimension(design, 'width', design.width + step);
        if (field === 'height') setDesignSizeFromDimension(design, 'height', design.height + step);
        design.sizeMode = 'custom';
        design.popularLabel = '';
        updateDesignCardValues(design);
      }
    });

    designsContainer?.addEventListener('change', (event) => {
      const target = event.target;
      const { design } = designFromEventTarget(target);
      if (!design) return;

      if (target.matches('[data-dtf-design-popular]')) {
        const size = popularSizes[Number(target.value)];
        design.sizeMode = 'popular';
        if (!size) {
          design.popularLabel = '';
          updateDesignCardValues(design);
          return;
        }
        design.width = size.width;
        design.height = size.height;
        design.popularLabel = size.label;
        setDesignAspectRatio(design, size.width, size.height);
        setStatus('', 'info');
        updateDesignCardValues(design);
      }
    });

    designsContainer?.addEventListener('input', (event) => {
      const target = event.target;
      const { design } = designFromEventTarget(target);
      if (!design) return;

      if (target.matches('[data-dtf-design-width]')) {
        if (target.value === '') return;
        if (!setDesignSizeFromDimension(design, 'width', target.value)) return;
        design.sizeMode = 'custom';
        clearPopularSelectionForCustomSize(design);
        updateDesignCardValues(design);
      } else if (target.matches('[data-dtf-design-height]')) {
        if (target.value === '') return;
        if (!setDesignSizeFromDimension(design, 'height', target.value)) return;
        design.sizeMode = 'custom';
        clearPopularSelectionForCustomSize(design);
        updateDesignCardValues(design);
      } else if (target.matches('[data-dtf-design-qty]')) {
        design.qty = parseQty(target.value, design.qty);
        updateDesignCardValues(design);
      }
    });

    designsContainer?.addEventListener('blur', (event) => {
      const target = event.target;
      const { design } = designFromEventTarget(target);
      if (!design) return;

      if (target.matches('[data-dtf-design-width]')) {
        if (target.value === '') target.value = round(design.width, 2).toFixed(2);
        if (setDesignSizeFromDimension(design, 'width', target.value)) {
          design.sizeMode = 'custom';
          clearPopularSelectionForCustomSize(design);
          updateDesignCardValues(design);
        }
      } else if (target.matches('[data-dtf-design-height]')) {
        if (target.value === '') target.value = round(design.height, 2).toFixed(2);
        if (setDesignSizeFromDimension(design, 'height', target.value)) {
          design.sizeMode = 'custom';
          clearPopularSelectionForCustomSize(design);
          updateDesignCardValues(design);
        }
      } else if (target.matches('[data-dtf-design-qty]')) {
        design.qty = parseQty(target.value, 1);
        updateDesignCardValues(design);
      }
    }, true);

    addButton?.addEventListener('click', () => addToCart({ redirectToCheckout: false }));
    checkoutLink?.addEventListener('click', () => addToCart({ redirectToCheckout: true }));
    aiPromptInput?.addEventListener('input', () => syncAiPromptWarning());
    aiDesignStyleSelect?.addEventListener('change', () => syncAiPromptWarning());
    aiOutputTypeSelect?.addEventListener('change', () => syncAiPromptWarning());
    aiIncludeTextInput?.addEventListener('change', () => syncAiPromptWarning());
    aiGenerateButton?.addEventListener('click', generateAiDesign);
    aiAddButton?.addEventListener('click', addGeneratedAiDesign);
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeZoomModal();
    });

    renderDesignCards();
    if (reorderId && reorderToken) {
      restoreFromReorderLink();
    }
  }

  function init(root = document) {
    root.querySelectorAll(builderSelector).forEach(initBuilder);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
}());
